/**
 * Per-step structured editor for `McpStep[]`.
 *
 * Replaces the legacy flat `<Textarea>` editors for `steps` (one line
 * per step) + `expectedResult` (single final assertion). After #596
 * the canonical step shape is `McpStep[]` — each step carries its own
 * `perform` (action prose) and `expect[]` (per-step assertions).
 *
 * #718 slice 1: `expect` is now an array of typed assertion clauses
 * (`TypedAssertion`), not free text. Each clause has a `kind` selector
 * and a kind-specific value control: a boolean toggle for
 * visible/checked/enabled, a text input for the *_equals / contains /
 * url kinds, a number input for count_equals, and a free-text input
 * for the `freeform` escape hatch. Switching kind resets to that
 * kind's default-shaped clause.
 *
 * Number checks: `number_saved` keeps a number under a name and
 * `number_compare` compares a later number with it. A compare row picks
 * the name from a list of names saved in EARLIER steps, never typing it,
 * so it cannot be misspelled. The editor computes that list per step and
 * flags a compare whose name no earlier step saves. The full set of rules
 * is `findSavedNumberProblems` below, a mirror of the server's, which the
 * parent runs before it lets a save start.
 *
 * Buffered state semantics live in the parent (TestDetail.tsx): this
 * component is a controlled input, dispatching every change up via
 * `onChange`. The parent owns dirty detection and save batching.
 */
import { Plus, Trash2, X } from 'lucide-react'
import { Button, Input, Label, Textarea } from '@/components/ui'
import type { McpStep, NumberCompareOp, NumberMeasure, TypedAssertion } from '@/types'

export interface McpStepsEditorProps {
  value: McpStep[]
  onChange: (next: McpStep[]) => void
  disabled?: boolean
}

/** All assertion kinds, in the order they appear in the kind dropdown. */
const ASSERTION_KINDS: TypedAssertion['kind'][] = [
  'visible',
  'checked',
  'enabled',
  'text_equals',
  'text_contains',
  'value_equals',
  'count_equals',
  'url_matches',
  'freeform',
  'number_saved',
  'number_compare',
]

/** Human-readable label for each kind, shown in the dropdown. */
const KIND_LABELS: Record<TypedAssertion['kind'], string> = {
  visible: 'is visible',
  checked: 'is checked',
  enabled: 'is enabled',
  text_equals: 'text equals',
  text_contains: 'text contains',
  value_equals: 'value equals',
  count_equals: 'count equals',
  url_matches: 'URL matches',
  freeform: 'freeform',
  number_saved: 'save a number',
  number_compare: 'compare a number',
}

/** How a number check reads its number, as the measure dropdown says it. */
const MEASURE_LABELS: Record<NumberMeasure, string> = {
  shown: 'number shown in it',
  elements: 'how many match',
}

/** The compare dropdown, in order. Same words `renderAssertion` uses. */
const COMPARE_OP_LABELS: Record<NumberCompareOp, string> = {
  greater_than: 'is more than',
  less_than: 'is less than',
  equals: 'equals',
  not_equals: 'does not equal',
}

/** A number some earlier step saves: the name as typed (trimmed) and the
 * 1-based step that saves it, for the "AND total (step 2)" option. */
interface SavedNumber {
  name: string
  step: number
}

/** Option values in the compare-with dropdown. Saved names are prefixed
 * so no name an operator types can collide with the fixed-number choice. */
const SAVED_OPTION_PREFIX = 'saved:'
const FIXED_NUMBER_OPTION = 'fixed'

/** A fresh clause of the given kind, with empty/default field values. */
function defaultAssertion(kind: TypedAssertion['kind']): TypedAssertion {
  switch (kind) {
    case 'visible':
    case 'checked':
    case 'enabled':
      return { kind, target: '', value: true }
    case 'text_equals':
    case 'text_contains':
    case 'value_equals':
      return { kind, target: '', value: '' }
    case 'count_equals':
      return { kind, target: '', value: 0 }
    case 'url_matches':
      return { kind, pattern: '' }
    case 'freeform':
      return { kind, text: '' }
    case 'number_saved':
      return { kind, target: '', measure: 'shown', name: '' }
    case 'number_compare':
      return { kind, target: '', measure: 'shown', op: 'greater_than', value: 0 }
  }
}

/** A compare clause pointed at a saved name or at a fixed number. Exactly
 * one of `against` and `value` survives, so the clause never holds both. */
function compareWith(
  a: Extract<TypedAssertion, { kind: 'number_compare' }>,
  pick: { against: string } | { value: number },
): TypedAssertion {
  const next = { ...a }
  delete next.against
  delete next.value
  return { ...next, ...pick }
}

/** For each step, the numbers saved in the steps BEFORE it. A compare may
 * only use those: a name saved in the same step or a later one does not
 * count. A repeated name keeps its first save, as the server's check
 * does. */
function savedNumbersBeforeEachStep(steps: McpStep[]): SavedNumber[][] {
  const before: SavedNumber[][] = []
  const saved: SavedNumber[] = []
  steps.forEach((step, i) => {
    before.push([...saved])
    for (const e of step.expect ?? []) {
      if (e.kind !== 'number_saved') continue
      const name = e.name.trim()
      if (name === '' || saved.some((s) => savedNumberKey(s.name) === savedNumberKey(name))) continue
      saved.push({ name, step: i + 1 })
    }
  })
  return before
}

/** Carry over `target` when switching between target-bearing kinds so
 * an operator changing "is visible" → "text contains" doesn't lose the
 * description they already typed. */
function changeKind(prev: TypedAssertion, kind: TypedAssertion['kind']): TypedAssertion {
  const next = defaultAssertion(kind)
  if ('target' in next && 'target' in prev) {
    next.target = prev.target
  }
  return next
}

const BOOL_KINDS = new Set<TypedAssertion['kind']>(['visible', 'checked', 'enabled'])
const TEXT_VALUE_KINDS = new Set<TypedAssertion['kind']>([
  'text_equals',
  'text_contains',
  'value_equals',
])

export function McpStepsEditor({ value, onChange, disabled }: McpStepsEditorProps) {
  const steps = value ?? []
  const savedBefore = savedNumbersBeforeEachStep(steps)

  function updateStep(i: number, patch: Partial<McpStep>) {
    onChange(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))
  }
  function updateExpect(stepIdx: number, expectIdx: number, next: TypedAssertion) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx
          ? { ...s, expect: s.expect.map((e, ei) => (ei === expectIdx ? next : e)) }
          : s,
      ),
    )
  }
  function addExpect(stepIdx: number) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx ? { ...s, expect: [...s.expect, defaultAssertion('visible')] } : s,
      ),
    )
  }
  function removeExpect(stepIdx: number, expectIdx: number) {
    onChange(
      steps.map((s, idx) =>
        idx === stepIdx ? { ...s, expect: s.expect.filter((_, ei) => ei !== expectIdx) } : s,
      ),
    )
  }
  function addStep() {
    onChange([...steps, { perform: '', expect: [] }])
  }
  function removeStep(i: number) {
    onChange(steps.filter((_, idx) => idx !== i))
  }

  if (steps.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground italic">No steps yet.</p>
        <Button variant="outline" size="sm" onClick={addStep} disabled={disabled}>
          <Plus className="h-3 w-3 mr-1" /> Add step
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/10">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Step {i + 1}</span>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon-sm"
              title="Remove step"
              onClick={() => removeStep(i)}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Action</Label>
            <Textarea
              value={step.perform ?? ''}
              onChange={(e) => updateStep(i, { perform: e.target.value })}
              rows={2}
              className="text-sm"
              placeholder="What this step does (e.g. 'Click the Search button')"
              disabled={disabled}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Expected outcomes</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => addExpect(i)}
                disabled={disabled}
              >
                <Plus className="h-3 w-3 mr-0.5" /> Add expect
              </Button>
            </div>
            {step.expect.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No assertions for this step (the step still runs; absence of expects means we don't verify state at this point).
              </p>
            ) : (
              <div className="space-y-1.5">
                {step.expect.map((assertion, ei) => (
                  <AssertionRow
                    key={ei}
                    assertion={assertion}
                    savedEarlier={savedBefore[i]}
                    disabled={disabled}
                    onChange={(next) => updateExpect(i, ei, next)}
                    onRemove={() => removeExpect(i, ei)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addStep} disabled={disabled}>
        <Plus className="h-3 w-3 mr-1" /> Add step
      </Button>
    </div>
  )
}

interface AssertionRowProps {
  assertion: TypedAssertion
  /** Numbers saved in earlier steps of this test, which a compare row
   * offers in its compare-with dropdown. */
  savedEarlier: SavedNumber[]
  disabled?: boolean
  onChange: (next: TypedAssertion) => void
  onRemove: () => void
}

/** One typed-assertion clause: a kind dropdown, the kind-specific
 * fields (target + value / pattern / text), and a remove button. */
function AssertionRow({ assertion, savedEarlier, disabled, onChange, onRemove }: AssertionRowProps) {
  const isNumberKind = assertion.kind === 'number_saved' || assertion.kind === 'number_compare'
  // A compare whose name no earlier step saves: the dropdown keeps showing
  // the name, marked as not saved, and a line under the row says why.
  const missingName =
    assertion.kind === 'number_compare' &&
    assertion.against !== undefined &&
    !savedEarlier.some((s) => savedNumberKey(s.name) === savedNumberKey(assertion.against ?? ''))
      ? assertion.against.trim()
      : null
  return (
    <div className="flex flex-wrap items-center gap-1 rounded border bg-background p-1.5">
      <select
        value={assertion.kind}
        onChange={(e) => onChange(changeKind(assertion, e.target.value as TypedAssertion['kind']))}
        disabled={disabled}
        className="h-7 rounded border bg-background px-1 text-xs"
        aria-label="Assertion kind"
      >
        {ASSERTION_KINDS.map((k) => (
          <option key={k} value={k}>
            {KIND_LABELS[k]}
          </option>
        ))}
      </select>

      {'target' in assertion && (
        <Input
          value={assertion.target}
          onChange={(e) => onChange({ ...assertion, target: e.target.value })}
          placeholder={
            isNumberKind ? "Target (e.g. 'the results total')" : "Target (e.g. 'the search-results heading')"
          }
          className="h-7 text-sm flex-1 min-w-[12rem]"
          disabled={disabled}
        />
      )}

      {(assertion.kind === 'number_saved' || assertion.kind === 'number_compare') && (
        <select
          value={assertion.measure}
          onChange={(e) => onChange({ ...assertion, measure: e.target.value as NumberMeasure })}
          disabled={disabled}
          className="h-7 rounded border bg-background px-1 text-xs"
          aria-label="Which number"
        >
          {(Object.keys(MEASURE_LABELS) as NumberMeasure[]).map((m) => (
            <option key={m} value={m}>
              {MEASURE_LABELS[m]}
            </option>
          ))}
        </select>
      )}

      {assertion.kind === 'number_saved' && (
        <>
          <span className="px-0.5 text-xs text-muted-foreground">as</span>
          <Input
            value={assertion.name}
            onChange={(e) => onChange({ ...assertion, name: e.target.value })}
            placeholder="Name (e.g. 'AND total')"
            className="h-7 text-sm w-32"
            aria-label="Saved number name"
            disabled={disabled}
          />
        </>
      )}

      {assertion.kind === 'number_compare' && (
        <>
          <select
            value={assertion.op}
            onChange={(e) => onChange({ ...assertion, op: e.target.value as NumberCompareOp })}
            disabled={disabled}
            className="h-7 rounded border bg-background px-1 text-xs"
            aria-label="How to compare"
          >
            {(Object.keys(COMPARE_OP_LABELS) as NumberCompareOp[]).map((op) => (
              <option key={op} value={op}>
                {COMPARE_OP_LABELS[op]}
              </option>
            ))}
          </select>
          <select
            value={
              assertion.against === undefined
                ? FIXED_NUMBER_OPTION
                : SAVED_OPTION_PREFIX +
                  (savedEarlier.find((s) => savedNumberKey(s.name) === savedNumberKey(assertion.against ?? ''))
                    ?.name ?? assertion.against)
            }
            onChange={(e) =>
              onChange(
                e.target.value === FIXED_NUMBER_OPTION
                  ? compareWith(assertion, { value: assertion.value ?? 0 })
                  : compareWith(assertion, { against: e.target.value.slice(SAVED_OPTION_PREFIX.length) }),
              )
            }
            disabled={disabled}
            className={`h-7 rounded border bg-background px-1 text-xs ${
              missingName !== null ? 'border-destructive text-destructive' : ''
            }`}
            aria-label="Compare with"
          >
            {savedEarlier.map((s) => (
              <option key={s.name} value={SAVED_OPTION_PREFIX + s.name}>
                {s.name} (step {s.step})
              </option>
            ))}
            {missingName !== null && (
              <option value={SAVED_OPTION_PREFIX + assertion.against}>{missingName} (not saved)</option>
            )}
            {(savedEarlier.length > 0 || missingName !== null) && (
              <option disabled value="">──────────</option>
            )}
            <option value={FIXED_NUMBER_OPTION}>a fixed number…</option>
          </select>
          {assertion.against === undefined && (
            <Input
              type="number"
              value={assertion.value ?? 0}
              onChange={(e) => {
                const n = Number(e.target.value)
                onChange(compareWith(assertion, { value: Number.isFinite(n) ? n : 0 }))
              }}
              className="h-7 text-sm w-24"
              aria-label="Fixed number"
              disabled={disabled}
            />
          )}
        </>
      )}

      {BOOL_KINDS.has(assertion.kind) && 'value' in assertion && (
        <select
          value={String(assertion.value)}
          onChange={(e) =>
            onChange({ ...assertion, value: e.target.value === 'true' } as TypedAssertion)
          }
          disabled={disabled}
          className="h-7 rounded border bg-background px-1 text-xs"
          aria-label="Expected value"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )}

      {TEXT_VALUE_KINDS.has(assertion.kind) && 'value' in assertion && (
        <Input
          value={String(assertion.value)}
          onChange={(e) => onChange({ ...assertion, value: e.target.value } as TypedAssertion)}
          placeholder="Expected text"
          className="h-7 text-sm flex-1 min-w-[8rem]"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'count_equals' && (
        <Input
          type="number"
          min={0}
          value={assertion.value}
          onChange={(e) =>
            onChange({ ...assertion, value: Math.max(0, Number(e.target.value) || 0) })
          }
          className="h-7 text-sm w-20"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'url_matches' && (
        <Input
          value={assertion.pattern}
          onChange={(e) => onChange({ ...assertion, pattern: e.target.value })}
          placeholder="URL substring or pattern"
          className="h-7 text-sm flex-1 min-w-[10rem]"
          disabled={disabled}
        />
      )}

      {assertion.kind === 'freeform' && (
        <Input
          value={assertion.text}
          onChange={(e) => onChange({ ...assertion, text: e.target.value })}
          placeholder="Free-text assertion"
          className="h-7 text-sm flex-1 min-w-[12rem]"
          disabled={disabled}
        />
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        title="Remove expect"
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-3 w-3" />
      </Button>

      {missingName !== null && (
        <p className="basis-full px-0.5 pt-0.5 text-xs text-destructive">
          No earlier step saves a number called "{missingName}". Pick another, or add a step that saves it.
        </p>
      )}
    </div>
  )
}

/** Canonical key order for serializing a `TypedAssertion`. Passed as the
 * `JSON.stringify` replacer so the output is independent of property
 * insertion order — two structurally-equal clauses then compare equal.
 * Mirrors `ASSERTION_CANONICAL_KEYS` in the server's `mcpStep.ts` (the
 * frontend cannot import server modules). */
const ASSERTION_KEY_ORDER = ['kind', 'pattern', 'target', 'text', 'value', 'measure', 'name', 'op', 'against']

/** Deep equality check for two McpStep[] arrays. Used by the parent
 * (TestDetail) for dirty detection — typed assertions are compared
 * structurally via canonical (key-order-independent) JSON; a direct
 * walk of the rest is cheap and unambiguous. */
export function mcpStepsEqual(a: McpStep[], b: McpStep[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if ((x.perform ?? '') !== (y.perform ?? '')) return false
    const xe = x.expect ?? []
    const ye = y.expect ?? []
    if (xe.length !== ye.length) return false
    for (let j = 0; j < xe.length; j++) {
      if (JSON.stringify(xe[j], ASSERTION_KEY_ORDER) !== JSON.stringify(ye[j], ASSERTION_KEY_ORDER)) return false
    }
  }
  return true
}

/** The key a saved-number name is matched by: trimmed, ignoring capitals.
 * Mirror of the server's `savedNumberKey` in `mcpStep.ts` (the frontend
 * cannot import server modules). */
export function savedNumberKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * Every rule a test's number checks must keep that one clause cannot
 * check alone, one plain sentence per problem. Empty when the test is
 * fine. Mirror of the server's `findSavedNumberProblems` in `mcpStep.ts`,
 * with the same messages word for word: the parent shows the first one
 * before a save starts, and the server refuses the save with the same
 * sentence if this check is ever skipped. Keep the two in step.
 */
export function findSavedNumberProblems(steps: unknown): string[] {
  if (!Array.isArray(steps)) return []
  const problems: string[] = []
  const savedAt = new Map<string, { step: number; name: string }>()
  steps.forEach((step: { expect?: unknown } | null | undefined, i) => {
    const n = i + 1
    const clauses = (Array.isArray(step?.expect) ? step.expect : []) as ReadonlyArray<Record<string, unknown> | null>
    // A comparison may only use names saved in an EARLIER step, so the
    // names this step saves join the map after its comparisons are read.
    const savedHere: Array<{ key: string; name: string }> = []
    for (const c of clauses) {
      if (c?.kind === 'number_compare') {
        // A field that is there counts as there, even blank (see the server).
        const hasAgainst = c.against !== undefined && c.against !== null
        const hasValue = c.value !== undefined && c.value !== null
        if (hasAgainst === hasValue) {
          problems.push(`Step ${n} has a number comparison that needs either a saved number or a fixed number, not both.`)
        } else if (hasAgainst && (typeof c.against !== 'string' || c.against.trim() === '')) {
          problems.push(`Step ${n} compares with a saved number but does not say which one.`)
        } else if (hasAgainst && !savedAt.has(savedNumberKey(c.against as string))) {
          problems.push(`Step ${n} compares with "${String(c.against).trim()}", but no earlier step saves a number by that name.`)
        } else if (hasValue && (typeof c.value !== 'number' || !Number.isFinite(c.value))) {
          problems.push(`Step ${n} compares with a fixed number that is not a number.`)
        }
      } else if (c?.kind === 'number_saved') {
        const name = typeof c.name === 'string' ? c.name.trim() : ''
        if (name === '') {
          problems.push(`Step ${n} saves a number without a name.`)
          continue
        }
        const key = savedNumberKey(name)
        const earlier = savedAt.get(key) ?? savedHere.find((s) => s.key === key)
        if (earlier) {
          const where = 'step' in earlier ? `step ${earlier.step}` : 'this step'
          problems.push(`Step ${n} saves a number called "${name}", but ${where} already saves one by that name.`)
          continue
        }
        savedHere.push({ key, name })
      }
    }
    for (const s of savedHere) savedAt.set(s.key, { step: n, name: s.name })
  })
  return problems
}
